import { useEffect, useState, useMemo } from 'react';
import flatMap from 'lodash/flatMap';
import maxBy from 'lodash/maxBy';
import type {
  ShowOccasionalDiscount,
  UseOccasionalDiscountReturn,
  DiscountPayload,
} from '@/types/discount';
import { usePostHog } from 'posthog-js/react';
import { UserContainer } from '@/providers';

/**
 * Custom React hook to manage and expose occasional discount information based on feature flags.
 *
 * This hook integrates with the PostHog feature flag system to determine if an occasional discount
 * should be shown to the user. It listens for the 'sd' feature flag, parses its payload (if enabled),
 * and extracts discount event and offer information. The hook manages loading state, the current
 * discount event, and computes the maximum discount available across all offers.
 *
 * @returns {UseOccasionalDiscountReturn} An object containing:
 *   - showOccasionalDiscount: {
 *       showInAppDiscountBanner: boolean; // Whether to display the in-app discount banner
 *       activeEvent: string | null;       // The current active discount event, if any
 *       offerDiscountMap: Record<string, unknown[]>; // Map of offers to their discount details
 *     }
 *   - isLoading: boolean;                 // Whether the hook is currently loading discount data
 *   - maxDiscount: number;                // The maximum discount value found across all offers
 *
 * @example
 * const { showOccasionalDiscount, isLoading, maxDiscount } = useOccasionalDiscount();
 *
 * @see DiscountPayload for the expected structure of the feature flag payload.
 */
export const useOccasionalDiscount = (): UseOccasionalDiscountReturn => {
  const posthog = usePostHog();
  const { state: userState } = UserContainer.useContainer();

  const [
    showOccasionalDiscount,
    setShowOccasionalDiscount,
  ] = useState<ShowOccasionalDiscount>({
    showInAppDiscountBanner: false,
    activeEvent: null,
    offerDiscountMap: {},
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function getFeatureFlag() {
      setIsLoading(true);

      if (!posthog) {
        setIsLoading(false);
        return;
      }

      posthog.onFeatureFlags(() => {
        if (posthog.isFeatureEnabled('sd')) {
          const payload = posthog.getFeatureFlagPayload('sd');
          let parsedPayload: DiscountPayload | null = null;
          try {
            const rawPayload =
              typeof payload === 'string' ? JSON.parse(payload) : payload;

            // Check if payload has structure with event and discounts
            if (
              rawPayload &&
              typeof rawPayload === 'object' &&
              'event' in rawPayload &&
              'discounts' in rawPayload
            ) {
              parsedPayload = rawPayload as DiscountPayload;
              setShowOccasionalDiscount({
                showInAppDiscountBanner: true,
                activeEvent: parsedPayload.event || null,
                offerDiscountMap: parsedPayload.discounts || {},
              });
            } else {
              // Invalid payload structure
              setShowOccasionalDiscount({
                showInAppDiscountBanner: false,
                activeEvent: null,
                offerDiscountMap: {},
              });
            }
          } catch (error) {
            console.error('Failed to parse feature flag payload:', error);
            setShowOccasionalDiscount({
              showInAppDiscountBanner: false,
              activeEvent: null,
              offerDiscountMap: {},
            });
          }
        } else {
          setShowOccasionalDiscount({
            showInAppDiscountBanner: false,
            activeEvent: null,
            offerDiscountMap: {},
          });
        }
        setIsLoading(false);
      });
    }
    getFeatureFlag();
  }, [posthog]);

  // Calculate max discount across all plans and billing cycles
  const maxDiscount = useMemo(() => {
    const offerDiscountMap = showOccasionalDiscount?.offerDiscountMap;
    const maxDiscountItem = offerDiscountMap
      ? maxBy(flatMap(offerDiscountMap), 'discount')
      : null;
    return (maxDiscountItem as { discount?: number } | null)?.discount || 0;
  }, [showOccasionalDiscount?.offerDiscountMap]);

  // Check if user is a plus annual user - don't show discount CTAs for them
  const disableDiscountDecorations = useMemo(() => {
    const planName =
      userState?.business?.billing?.paddleSubscription?.planName?.toLowerCase() ||
      '';
    const billingPeriod =
      userState?.business?.billing?.paddleSubscription?.billingPeriod;
    return planName.includes('team') && billingPeriod === 'year';
  }, [userState?.business?.billing?.paddleSubscription]);

  return {
    showOccasionalDiscount,
    isLoading,
    maxDiscount,
    disableDiscountDecorations,
  };
};
